
import { useParams, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import SEOHead from '@/components/SEOHead';
import LocationSection from '@/components/LocationSection';
import ReviewsSection from '@/components/ReviewsSection';
import PropertyHeader from '@/components/PropertyHeader';
import PropertyGallery from '@/components/PropertyGallery';
import PropertyInfo from '@/components/PropertyInfo';
import PropertyHost from '@/components/PropertyHost';
import PropertyHighlights from '@/components/PropertyHighlights';
import PropertyDescription from '@/components/PropertyDescription';
import PropertyAmenities from '@/components/PropertyAmenities';
import BookingCard from '@/components/BookingCard';
import PropertyCalendar from '@/components/PropertyCalendar';
import MobileBookingBar from '@/components/MobileBookingBar';
import { usePropertyBySlug } from '@/hooks/usePropertyBySlug';
import { usePropertySEOData } from '@/hooks/usePropertySEOData';
import { useSupabaseAvailability } from '@/hooks/useSupabaseAvailability';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';
import { getPropertyShortName } from '@/utils/propertyUtils';

// Helper function to parse date string safely (YYYY-MM-DD format)
const parseDateFromUrl = (dateString: string): Date | null => {
  if (!dateString) return null;

  // Split the date string to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return null;

  // Create date in local timezone (month is 0-indexed)
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime()) ? null : date;
};

const PropertyDetail = () => {
  const { title: titleParam } = useParams();
  const [searchParams] = useSearchParams();
  const [selectedCheckIn, setSelectedCheckIn] = useState<Date | undefined>(undefined);
  const [selectedCheckOut, setSelectedCheckOut] = useState<Date | undefined>(undefined);
  const { trackPropertyDetailViewed } = useAmplitudeEvents();
  
  // Use ref to track if event has been fired for current property
  const eventTrackedRef = useRef<string>('');

  // Get SEO data immediately (static data first, then fallback)
  const seoData = usePropertySEOData(titleParam || '');

  // Get property by slug from Supabase
  const { data: property, isLoading, error } = usePropertyBySlug(titleParam || '');
  
  // Get availability data from Supabase using the property ID
  const { unavailableDates, isLoading: availabilityLoading, error: availabilityError } = useSupabaseAvailability(property?.id || '');

  // Track property detail viewed when property is loaded - ONLY ONCE per property
  useEffect(() => {
    if (property && !isLoading) {
      const propertyKey = `${property.id}-${property.title}`;
      
      // Only fire if we haven't fired for this exact property
      if (eventTrackedRef.current !== propertyKey) {
        const propertyShortName = getPropertyShortName(property.title);
        const totalReviews = property.reviews ? property.reviews.length : 0;
        const totalPhotos = property.images ? property.images.length : 0;
        const hostName = property.host ? property.host.name : '';

        trackPropertyDetailViewed(
          property.title,
          propertyShortName,
          property.rating,
          property.guests,
          property.price,
          totalReviews,
          totalPhotos,
          hostName
        );
        
        eventTrackedRef.current = propertyKey;
      }
    }
  }, [property, isLoading, trackPropertyDetailViewed]);

  // Initialize dates from URL parameters on mount
  useEffect(() => {
    const checkinParam = searchParams.get('checkin');
    const checkoutParam = searchParams.get('checkout');
    
    if (checkinParam) {
      const checkinDate = parseDateFromUrl(checkinParam);
      if (checkinDate) {
        setSelectedCheckIn(checkinDate);
      }
    }
    if (checkoutParam) {
      const checkoutDate = parseDateFromUrl(checkoutParam);
      if (checkoutDate) {
        setSelectedCheckOut(checkoutDate);
      }
    }
  }, [searchParams]);

  if (isLoading) {
    return (
      <Layout>
        <SEOHead 
          title={seoData.title}
          description={seoData.description}
          canonicalUrl={seoData.canonicalUrl}
          image={seoData.image}
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando propiedad...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !property) {
    return (
      <Layout>
        <SEOHead 
          title="Propiedad no encontrada | Estancia Corta"
          description="La propiedad que buscas no está disponible. Explora otros alojamientos en Manzanillo."
          canonicalUrl={seoData.canonicalUrl}
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Propiedad no encontrada</h1>
            <p className="text-gray-600">La propiedad que buscas no existe.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const handleDateChange = (checkIn: Date | undefined, checkOut: Date | undefined) => {
    setSelectedCheckIn(checkIn);
    setSelectedCheckOut(checkOut);
  };

  return (
    <Layout>
      <SEOHead 
        title={seoData.title}
        description={seoData.description}
        canonicalUrl={seoData.canonicalUrl}
        image={seoData.image}
      />
      
      <PropertyHeader 
        title={property.title} 
        rating={property.rating} 
        neighborhood={property.neighborhood}
        guests={property.guests} 
        bedrooms={property.bedrooms} 
        bathrooms={property.bathrooms} 
      />

      <PropertyGallery images={property.images} title={property.title} />

      <section className="pb-2 md:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="">
                <div className="">
                  <PropertyInfo guests={property.guests} bedrooms={property.bedrooms} bathrooms={property.bathrooms} />
                </div>

                <div className="py-6 border-b border-gray-200">
                  <PropertyHost host={property.host} />
                  <PropertyHighlights highlights={property.propertyHighlights} />
                </div>

                <PropertyDescription description={property.longDescription} />
                <PropertyAmenities propertyId={property.id} />
                <LocationSection property={property} />

                <PropertyCalendar 
                  onDateChange={handleDateChange} 
                  selectedCheckIn={selectedCheckIn} 
                  selectedCheckOut={selectedCheckOut}
                  unavailableDates={unavailableDates}
                />

                {property.reviews && property.reviews.length > 0 && <ReviewsSection reviews={property.reviews} />}
              </div>
            </div>

            <div className="lg:col-span-1">
              <BookingCard 
                price={property.price} 
                checkIn={selectedCheckIn} 
                checkOut={selectedCheckOut} 
                onDateChange={handleDateChange} 
                maxGuests={property.guests} 
                propertyTitle={property.title} 
                propertyId={property.id}  // Pass the correct property ID
                unavailableDates={unavailableDates}
                airbnbUrl={property.airbnbUrl}
                property={{
                  rating: property.rating,
                  guests: property.guests,
                  reviews: property.reviews,
                  images: property.images,
                  host: property.host
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Booking Bar - Solo visible en móvil */}
      <MobileBookingBar 
        price={property.price} 
        checkIn={selectedCheckIn} 
        checkOut={selectedCheckOut} 
        onDateChange={handleDateChange} 
        unavailableDates={unavailableDates}
        propertyTitle={property.title}
        property={{
          rating: property.rating,
          guests: property.guests,
          reviews: property.reviews,
          images: property.images,
          host: property.host
        }}
        maxGuests={property.guests}
      />
    </Layout>
  );
};

export default PropertyDetail;
